/**
 * Product Information Audit Page
 * 
 * Administrative interface for viewing audit trails, version history,
 * and managing product information changes across all batches.
 */

import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ProductInfoAuditTrail } from '../components/ProductInfoAuditTrail';
import { ProductInfoVersionComparison } from '../components/ProductInfoVersionComparison';
import { formatBatchName } from '../lib/utils';
import {
  ProductInfoVersion,
  AuditTrailQuery,
  AuditTrailResponse
} from '../types/enhanced-product-info.types';
import { productInfoAuditService } from '../services/product-info-audit.service';
import { enhancedProductService } from '../services/enhanced-product.service';

interface AuditStats {
  timeframe: string;
  totalChanges: number;
  changesByType: Record<string, number>;
  changesByDay: Record<string, number>;
  topContributors: Record<string, number>;
  batchesModified: number;
}

export const ProductInfoAuditPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'search' | 'batch-audit' | 'versions'>('overview');
  const [auditStats, setAuditStats] = useState<AuditStats | null>(null);
  const [searchResults, setSearchResults] = useState<AuditTrailResponse | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<string>('');
  const [batchVersions, setBatchVersions] = useState<ProductInfoVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    changeType: '',
    changedBy: '',
    startDate: '',
    endDate: '',
    timeframe: '30d'
  });

  useEffect(() => {
    loadAuditStats();
  }, [searchFilters.timeframe]);

  const loadAuditStats = async () => {
    try {
      setLoading(true);
      const stats = await productInfoAuditService.getAuditStats(searchFilters.timeframe);
      setAuditStats(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);

      const query: AuditTrailQuery = {
        page: 1,
        limit: 100
      };

      if (searchFilters.changeType) {
        query.changeType = searchFilters.changeType as any;
      }

      if (searchFilters.changedBy) {
        query.changedBy = searchFilters.changedBy;
      }

      if (searchFilters.startDate) {
        query.startDate = new Date(searchFilters.startDate);
      }

      if (searchFilters.endDate) {
        query.endDate = new Date(searchFilters.endDate);
      }

      const results = await productInfoAuditService.getAuditTrail(query);
      setSearchResults(results);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchVersions = async (batchId: string) => {
    try {
      setLoading(true);
      const versions = await enhancedProductService.getVersionHistory(batchId);
      setBatchVersions(versions);
      setSelectedBatchId(batchId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      {auditStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-2xl">📊</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Changes</p>
                <p className="text-2xl font-semibold text-gray-900">{auditStats.totalChanges}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-2xl">📦</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Batches Modified</p>
                <p className="text-2xl font-semibold text-gray-900">{auditStats.batchesModified}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <span className="text-2xl">👥</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Contributors</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {Object.keys(auditStats.topContributors).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <span className="text-2xl">📈</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Daily Changes</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {Math.round(auditStats.totalChanges / Object.keys(auditStats.changesByDay).length || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeframe Selector */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Activity Overview</h3>
          <select
            value={searchFilters.timeframe}
            onChange={(e) => setSearchFilters(prev => ({ ...prev, timeframe: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>

        {auditStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Changes by Type */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Changes by Type</h4>
              <div className="space-y-2">
                {Object.entries(auditStats.changesByType).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">
                      {type.replace('_', ' ')}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Contributors */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Top Contributors</h4>
              <div className="space-y-2">
                {Object.entries(auditStats.topContributors)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([contributor, count]) => (
                    <div key={contributor} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{contributor}</span>
                      <span className="text-sm font-medium text-gray-900">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSearchTab = () => (
    <div className="space-y-6">
      {/* Search Filters */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Search Audit Trail</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Change Type
            </label>
            <select
              value={searchFilters.changeType}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, changeType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="images">Images</option>
              <option value="nutrition">Nutrition</option>
              <option value="custom_content">Custom Content</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="rollback">Rollback</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Changed By
            </label>
            <input
              type="text"
              value={searchFilters.changedBy}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, changedBy: e.target.value }))}
              placeholder="User ID or email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={searchFilters.startDate}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={searchFilters.endDate}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Search Results ({searchResults.pagination.totalCount} records)
            </h3>
          </div>
          
          <div className="p-6">
            {searchResults.auditTrail.length > 0 ? (
              <div className="space-y-4">
                {searchResults.auditTrail.map((audit) => (
                  <div key={audit.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          audit.changeType === 'images' ? 'bg-blue-100 text-blue-800' :
                          audit.changeType === 'nutrition' ? 'bg-green-100 text-green-800' :
                          audit.changeType === 'custom_content' ? 'bg-purple-100 text-purple-800' :
                          audit.changeType === 'manufacturing' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {audit.changeType.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-600">
                          {format(new Date(audit.changedAt), 'MMM dd, yyyy HH:mm')}
                        </span>
                      </div>
                      
                      {audit.batch && (
                        <div className="text-sm text-gray-600">
                          {formatBatchName(
                            audit.batch.productName,
                            audit.batch.createdAt,
                            audit.batch.batchTitle
                          )} - {audit.batch.manufacturerName}
                        </div>
                      )}
                    </div>
                    
                    {audit.changer && (
                      <div className="mt-2 text-sm text-gray-600">
                        Changed by: {audit.changer.fullName} ({audit.changer.email})
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No audit records found matching your search criteria.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderBatchAuditTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Batch Audit Trail</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter Batch ID
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
              placeholder="Enter batch ID to view audit trail"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => selectedBatchId && setActiveTab('batch-audit')}
              disabled={!selectedBatchId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Load Audit Trail
            </button>
          </div>
        </div>
      </div>

      {selectedBatchId && (
        <ProductInfoAuditTrail batchId={selectedBatchId} />
      )}
    </div>
  );

  const renderVersionsTab = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Version Comparison</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter Batch ID
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={selectedBatchId}
              onChange={(e) => setSelectedBatchId(e.target.value)}
              placeholder="Enter batch ID to view versions"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={() => selectedBatchId && loadBatchVersions(selectedBatchId)}
              disabled={!selectedBatchId || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load Versions'}
            </button>
          </div>
        </div>
      </div>

      {batchVersions.length > 0 && (
        <ProductInfoVersionComparison
          batchId={selectedBatchId}
          versions={batchVersions}
          onRollback={(result) => {
            console.log('Rollback completed:', result);
            // Refresh versions after rollback
            loadBatchVersions(selectedBatchId);
          }}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Product Information Audit</h1>
          <p className="mt-2 text-gray-600">
            Monitor changes, view version history, and manage product information across all batches.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'search', label: 'Search', icon: '🔍' },
              { id: 'batch-audit', label: 'Batch Audit', icon: '📋' },
              { id: 'versions', label: 'Versions', icon: '🔄' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'search' && renderSearchTab()}
        {activeTab === 'batch-audit' && renderBatchAuditTab()}
        {activeTab === 'versions' && renderVersionsTab()}
      </div>
    </div>
  );
};

export default ProductInfoAuditPage;
